import React from 'react'
import { NavLink } from 'react-router-dom'
import {
    createStyles,
    ListItem,
    ListItemIcon,
    Theme,
    makeStyles,
    Tooltip,
    Typography,
} from '@material-ui/core'
import clsx from 'clsx'
import { NavigationItem } from '../types/navigation'
import { useUser } from '../../hooks'
import { VENDOR } from '../../helpers'

type StyleProps = {
    isLightSidebar: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) =>
    createStyles({
        highlightMenu: ({ isLightSidebar }) => ({
            borderLeft: '6px solid #008c85 !important',
            '& path': {
                stroke: isLightSidebar
                    ? `${theme.palette.primary.dark} !important`
                    : '#FFF !important',
            },
            '& svg': {
                color: isLightSidebar
                    ? `${theme.palette.primary.dark} !important`
                    : '#FFF !important',
            },
            '& $navCaption': {
                color: `${theme.palette.primary.dark} !important`,
            },
        }),
        navMenu: ({ isLightSidebar }) => ({
            borderLeft: '6px solid transparent',
            '& path': {
                stroke: isLightSidebar ? theme.palette.darkBlue.main : '#FFF',
            },
            '& svg': {
                color: isLightSidebar ? theme.palette.darkBlue.main : '#FFF',
            },
        }),
        sideBarItem: {
            maxHeight: 'calc(100vh - 114px) !important',
            overflowY: 'auto',
            '& .MuiListItem-root': {
                paddingTop: '16px',
                paddingBottom: '16px',
            },
        },
        footerText: {
            fontSize: '1.25rem',
            lineHeight: '24px',
            fontWeight: 600,
            color: '#FFF',
            margin: 'auto',
            padding: '15px 0',
            textAlign: 'center',
        },
        toolTipMenuIcon: {
            background: '#FFF',
            color: '#000',
            fontSize: '0.813rem',
            fontWeight: 400,
            maxWidth: '104px',
            textAlign: 'center',
        },
        toolTipVendorNav: {
            background: '#FFF',
            color: '#000',
            fontSize: '0.813rem',
            fontWeight: 400,
            maxWidth: 280,
            textAlign: 'center',
        },
        listItem: {
            padding: 0,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            minWidth: 'fit-content',
            width: 'auto',
        },
        listItemVendor: {
            padding: 0,
            minHeight: 'unset',
            alignItems: 'stretch',
            width: '100%',
        },
        vendorColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            padding: theme.spacing(0.75, 0.25, 0.5),
            boxSizing: 'border-box',
        },
        iconSlot: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
        },
        navCaption: ({ isLightSidebar }) => ({
            marginTop: theme.spacing(0.25),
            fontSize: '0.65rem',
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: '0.06em',
            textAlign: 'center',
            textTransform: 'uppercase',
            fontFamily:
                '"Arial Black", "Helvetica Neue", "Arial Narrow", Arial, sans-serif',
            color: isLightSidebar ? theme.palette.darkBlue.main : '#FFF',
            maxWidth: '100%',
            overflowWrap: 'anywhere',
        }),
        listItemIcon: ({ isLightSidebar }) => ({
            color: isLightSidebar ? theme.palette.darkBlue.main : '#FFF',
            justifyContent: 'center',
            minWidth: 48,
            width: 'auto',
            margin: 0,
        }),
    }),
)

const NavItem = ({ item }: { item: NavigationItem }) => {
    const { workspaceUser } = useUser()
    const isLightSidebar =
        workspaceUser?.active_workspace.company_type === VENDOR

    const classes = useStyles({ isLightSidebar })
    const labelText = (item.navLabel ?? item.name).toUpperCase()

    if (isLightSidebar) {
        const tooltipTitle = item.tooltipTitle ?? item.name

        return (
            <ListItem
                component={NavLink}
                to={item.link}
                button
                className={clsx(
                    classes.navMenu,
                    classes.listItem,
                    classes.listItemVendor,
                )}
                activeClassName={classes.highlightMenu}
                aria-label={item.name}
            >
                <Tooltip
                    title={tooltipTitle}
                    placement="top"
                    classes={{ tooltip: classes.toolTipVendorNav }}
                >
                    <div className={classes.vendorColumn}>
                        <span className={classes.iconSlot}>{item.icon}</span>
                        <Typography
                            component="span"
                            variant="caption"
                            display="block"
                            className={classes.navCaption}
                        >
                            {labelText}
                        </Typography>
                    </div>
                </Tooltip>
            </ListItem>
        )
    }

    return (
        <ListItem
            component={NavLink}
            to={item.link}
            button
            className={clsx(classes.navMenu, classes.listItem)}
            activeClassName={classes.highlightMenu}
            aria-label={item.name}
        >
            <Tooltip
                title={item.name}
                placement="top"
                classes={{ tooltip: classes.toolTipMenuIcon }}
            >
                <ListItemIcon className={classes.listItemIcon}>
                    {item.icon}
                </ListItemIcon>
            </Tooltip>
        </ListItem>
    )
}

export default NavItem
